import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Auth is now cookie-based (httpOnly `rekisa_admin` set by backend) with a
// double-submit CSRF token (`rekisa_csrf` cookie mirrored in X-CSRF-Token
// header on mutating requests). We no longer store the JWT in localStorage.
const CSRF_COOKIE = "rekisa_csrf";
const CSRF_HEADER = "X-CSRF-Token";

function readCookie(name) {
  const rows = document.cookie ? document.cookie.split("; ") : [];
  for (const row of rows) {
    const eq = row.indexOf("=");
    if (eq === -1) continue;
    if (row.slice(0, eq) === name) return decodeURIComponent(row.slice(eq + 1));
  }
  return "";
}

const AdminContext = createContext(null);

export function AdminProvider({ children }) {
  // null = still checking session, false = unauthenticated, object = authenticated
  const [admin, setAdmin] = useState(null);
  const [error, setError] = useState(null);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/auth/me`, {
        withCredentials: true,
      });
      setAdmin(data);
    } catch {
      setAdmin(false);
    }
  }, []);

  const { pathname } = useLocation();
  const fetchedRef = useRef(false);
  // Only check the admin session on admin routes (keeps public landing pages free of the extra request).
  useEffect(() => {
    if (!pathname.startsWith("/admin") || fetchedRef.current) return;
    fetchedRef.current = true;
    fetchMe();
  }, [pathname, fetchMe]);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setAdmin(data.admin);
      return true;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed");
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/auth/logout`,
        {},
        {
          withCredentials: true,
          headers: { [CSRF_HEADER]: readCookie(CSRF_COOKIE) },
        }
      );
    } catch {
      // Logout is best-effort; clear local state regardless.
    }
    setAdmin(false);
  }, []);

  const authedFetch = useCallback(
    async (path, opts = {}) => {
      const method = (opts.method || "GET").toUpperCase();
      const needsCsrf = ["POST", "PATCH", "PUT", "DELETE"].includes(method);
      const res = await fetch(`${BACKEND_URL}/api${path}`, {
        ...opts,
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(needsCsrf ? { [CSRF_HEADER]: readCookie(CSRF_COOKIE) } : {}),
          ...(opts.headers || {}),
        },
      });
      if (res.status === 401) {
        setAdmin(false);
        throw new Error("Session expired");
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const detail = body?.detail;
        throw new Error(typeof detail === "string" ? detail : `Request failed (${res.status})`);
      }
      if (res.status === 204) return null;
      const text = await res.text();
      return text ? JSON.parse(text) : null;
    },
    []
  );

  return (
    <AdminContext.Provider value={{ admin, login, logout, error, authedFetch }}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const ctx = useContext(AdminContext);
  if (!ctx) throw new Error("useAdmin must be used inside AdminProvider");
  return ctx;
}
